import React from 'react';
import { Settings, Zap, Wrench, ClipboardList, Package, Briefcase, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { services } from '../mock';

const iconMap = {
  settings: Settings,
  zap: Zap,
  wrench: Wrench,
  clipboardList: ClipboardList,
  package: Package,
  briefcase: Briefcase
};

const Services = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-slate-900 to-blue-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Layanan Kami</h1>
            <p className="text-xl text-blue-100">
              Solusi lengkap dan profesional untuk kebutuhan konstruksi mekanikal dan elektrikal Anda
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => {
                const IconComponent = iconMap[service.icon];
                return (
                  <Card key={service.id} className="bg-white border-2 border-slate-200 hover:border-blue-500 hover:shadow-2xl transition-all duration-300 group overflow-hidden">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                      <div className="absolute bottom-4 left-4">
                        <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">{service.title}</h3>
                      <p className="text-slate-600 leading-relaxed mb-4 text-sm">{service.description}</p>
                      <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:gap-2 transition-all duration-300">
                        <span>Detail Layanan</span>
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-2 transition-transform duration-300" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Service Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Proses Kerja Kami</h2>
              <p className="text-lg text-slate-600">
                Tahapan sistematis untuk memastikan proyek Anda berjalan lancar
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: '01', title: 'Konsultasi', desc: 'Diskusi kebutuhan dan perencanaan proyek' },
                { step: '02', title: 'Survei', desc: 'Kunjungan lokasi dan analisis teknis' },
                { step: '03', title: 'Pelaksanaan', desc: 'Eksekusi proyek dengan tim profesional' },
                { step: '04', title: 'Serah Terima', desc: 'Inspeksi akhir dan garansi pekerjaan' }
              ].map((process, index) => (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold shadow-lg">
                    {process.step}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{process.title}</h3>
                  <p className="text-slate-600 text-sm">{process.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Keunggulan Layanan Kami</h2>
              <p className="text-lg text-slate-600">
                Komitmen kami untuk memberikan yang terbaik
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: 'Material Berkualitas', desc: 'Menggunakan material dari supplier terpercaya dan berstandar SNI' },
                { title: 'Tenaga Ahli Bersertifikat', desc: 'Tim teknisi dan engineer yang kompeten dan berpengalaman' },
                { title: 'Harga Transparan', desc: 'RAB detail dan tidak ada biaya tersembunyi' },
                { title: 'Garansi Pekerjaan', desc: 'Jaminan kualitas dan layanan purna jual' },
                { title: 'Tepat Waktu', desc: 'Manajemen proyek yang efisien dan deadline terjaga' },
                { title: 'K3 Terjamin', desc: 'Prosedur keselamatan kerja yang ketat di setiap proyek' }
              ].map((feature, index) => (
                <div key={index} className="flex items-start bg-white p-6 rounded-lg border border-slate-200 hover:border-blue-400 hover:shadow-lg transition-all duration-300">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600 text-sm">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Tertarik dengan Layanan Kami?</h2>
            <p className="text-xl mb-10 text-blue-100">
              Konsultasikan kebutuhan proyek Anda dengan tim profesional kami
            </p>
            <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300" asChild>
              <Link to="/kontak">
                Hubungi Kami Sekarang
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
